// @ts-strict-ignore
import {computed, inject, Injectable, signal} from "@angular/core";
import {DeviceActivityType, ExamMasterOfCeremonyApplicationData, SessionPhaseType} from "@models";
import {UntilDestroy} from "@ngneat/until-destroy";
import {EventEmitterService} from "@event-emitter";
import {PhaseTypeMapping, TypePredicates} from "@services";
import {SimTimeService} from "../../../../core/services/sim-services/sim-time.service";
import {PhaseColorMapping} from "../../../../shared/models/SimSessionPhasePalette";
import {ActivatedRoute} from "@angular/router";
import {AppDataSyncService} from "@common/services/app-data-sync.service";

@UntilDestroy()
@Injectable({
	providedIn: "root",
})
export class ExamMcStateService {
	// Signals
	public isLoading = signal<boolean>(true);
	public errorState = signal<boolean>(false);
	public isRemoteExamination = signal<boolean>(false);
	public state = signal<ExamMasterOfCeremonyApplicationData>(null);
	public timerElapsed = signal<number>(0);
	public phaseTheme = computed<string>(() => this._computePhaseTheme());
	public phaseStartTimeAdjusted = computed(() => this._timeService.getAdjustedLocalTime(this.state()?.phaseStartTime));

	// Injections
	private _eventEmitter = inject(EventEmitterService);
	private _timeService = inject(SimTimeService);
	private _route = inject(ActivatedRoute);
	readonly #syncService = inject(AppDataSyncService);

	public initState() {
		// Disable standard header
		this._eventEmitter.onShowNavigation.next({visible: false});

		// Sub to websocket
		this._eventEmitter.onApplicationDataChanged.subscribe(
			async ({applicationData}: {applicationData: ExamMasterOfCeremonyApplicationData}) => {
				// Validate
				if (!this._isApplicationDataValid(applicationData)) {
					this.errorState.set(true);
					return;
				}

				// Set state
				this.state.set(applicationData);
				this.isLoading.set(false);
				this.errorState.set(false);
				this._startTimer();
			},
		);

		// Check if disableControls
		this._route.queryParams.subscribe((params) => this.isRemoteExamination.set(!!params["remoteExamination"]));
		this.#syncService.preventInfiniteLoading(this.isLoading, DeviceActivityType.ExamMasterOfCeremony);
	}

	private _startTimer() {
		// Prevent unwanted overriding of val
		if (!TypePredicates.isExamMcRunApplicationData(this.state()) || this.timerElapsed() > 0) return;
		// Count up
		setInterval(() => {
			const adjustedSessionStartTime = this._timeService.getAdjustedLocalTime(
				this.state().firstRunStartTime ?? this.state().sessionStartTime,
			);
			this.timerElapsed.set(Date.now() - adjustedSessionStartTime.getTime());
		}, 100);
	}

	private _computePhaseTheme(): string {
		const phaseType = this.state()?.type in PhaseTypeMapping ? PhaseTypeMapping[this.state().type] : -1;
		return PhaseColorMapping[phaseType as SessionPhaseType];
	}

	private _isApplicationDataValid(applicationData: ExamMasterOfCeremonyApplicationData): boolean {
		const result = !!(applicationData && applicationData.stations?.length);
		if (!result) {
			console.error("Invalid application data received");
			console.table(applicationData);
		}
		return result;
	}
}
